import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  ArrowLeft,
  Truck,
  Shield,
  Award,
  AlertCircle,
  CheckCircle,
  Loader2,
  User,
  MapPin,
  Mail,
  Phone,
} from "lucide-react";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import { useCart } from "../../contexts/CartContext";
import "./checkout.css";

/**
 * Secure Checkout — redesigned public page (route: /checkout).
 *
 * Visual/interaction language: mirrors ROnlineStorePage / RBookTourPage
 * (dark cards, accent CTAs, form-left / sticky-summary-right).
 *
 * Functional ground truth: frontend/src/pages/NewCheckoutPage.js — every
 * field, validation rule, the exact Stripe checkout-session fetch
 * (`POST ${REACT_APP_BACKEND_URL}/api/checkout/session`), request body shape,
 * response handling, redirect, empty-cart handling, loading/error/success
 * states and order-summary math are preserved verbatim. Revenue-critical page:
 * the payment flow is untouched — only restyled and re-laid-out.
 */

const RCheckoutPage = () => {
  const {
    items,
    totalItems,
    subtotal,
    formatPrice,
    getShippingFee,
    getTotal,
  } = useCart();

  const navigate = useNavigate();

  // Australian states and territories
  const australianStates = [
    { code: "NSW", name: "New South Wales" },
    { code: "VIC", name: "Victoria" },
    { code: "QLD", name: "Queensland" },
    { code: "WA", name: "Western Australia" },
    { code: "SA", name: "South Australia" },
    { code: "TAS", name: "Tasmania" },
    { code: "ACT", name: "Australian Capital Territory" },
    { code: "NT", name: "Northern Territory" },
  ];

  const [customerInfo, setCustomerInfo] = useState({
    // Customer Details
    firstName: "",
    lastName: "",
    email: "",
    phone: "",

    // Delivery Address
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "NSW",
    postalCode: "",
    country: "Australia",
    deliveryNotes: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const shippingFee = getShippingFee();
  const total = getTotal();

  // Redirect if cart is empty
  useEffect(() => {
    if (items.length === 0) {
      navigate("/opal-store");
    }
  }, [items.length, navigate]);

  const handleInputChange = (field, value) => {
    setCustomerInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError(""); // Clear errors when user types
  };

  const validateForm = () => {
    const required = [
      "firstName",
      "lastName",
      "email",
      "phone",
      "addressLine1",
      "city",
      "state",
      "postalCode",
    ];

    for (let field of required) {
      if (!customerInfo[field].trim()) {
        const fieldName = field
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());
        setError(`Please enter your ${fieldName.toLowerCase()}`);
        return false;
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerInfo.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    // Australian postal code validation (4 digits)
    const postalRegex = /^\d{4}$/;
    if (!postalRegex.test(customerInfo.postalCode)) {
      setError("Please enter a valid 4-digit Australian postal code");
      return false;
    }

    return true;
  };

  const handleCheckout = async () => {
    setError("");
    setSuccess("");

    if (!validateForm()) {
      return;
    }

    if (items.length === 0) {
      setError("Your cart is empty");
      return;
    }

    setLoading(true);

    try {
      // Prepare cart data for backend
      const cartItems = items.map((item) => ({
        product_id: item.product._id || item.product.id,
        product_name: item.product.name,
        product_image:
          item.product.images?.[0]?.src || item.product.images?.[0] || "",
        quantity: item.quantity,
        price: parseFloat(item.product.price || item.product.regular_price || 0),
      }));

      // Prepare checkout data
      const checkoutData = {
        cart_items: cartItems,
        customer_email: customerInfo.email,
        customer_name: `${customerInfo.firstName} ${customerInfo.lastName}`,
        customer_phone: customerInfo.phone,
        shipping_fee: shippingFee, // Include shipping fee
        origin_url: window.location.origin,
        shipping_address: {
          first_name: customerInfo.firstName,
          last_name: customerInfo.lastName,
          address_line_1: customerInfo.addressLine1,
          address_line_2: customerInfo.addressLine2,
          city: customerInfo.city,
          state: customerInfo.state,
          postal_code: customerInfo.postalCode,
          country: customerInfo.country,
          phone: customerInfo.phone,
        },
        customer_notes: customerInfo.deliveryNotes,
      };

      // Create Stripe checkout session
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/checkout/session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(checkoutData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to create checkout session");
      }

      const { checkout_url } = await response.json();

      // Redirect to Stripe checkout
      window.location.href = checkout_url;
    } catch (err) {
      console.error("Checkout error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const seo = (
    <SEOHead
      title="Secure Checkout"
      description="Securely complete your Umoona Opal Mine order. Certified genuine Australian opals, flat-rate Australian shipping, payment powered by Stripe."
      canonical="/checkout"
      noIndex={true}
    />
  );

  // Empty cart fallback (matches NewCheckoutPage empty state; the useEffect
  // above also redirects to /opal-store).
  if (items.length === 0) {
    return (
      <MarketingShell pageClass="pg-checkout">
        {seo}
        <section className="u-sect-deep ck-emptywrap">
          <div className="ck-empty">
            <div className="ck-empty__icon">
              <ShoppingCart size={44} />
            </div>
            <h1 className="ck-empty__title">Your cart is empty</h1>
            <p className="ck-empty__text">
              Add some beautiful opals to your cart to continue
            </p>
            <Link to="/opal-store" className="u-btn u-btn--primary">
              Browse Products
            </Link>
          </div>
        </section>
      </MarketingShell>
    );
  }

  return (
    <MarketingShell pageClass="pg-checkout">
      {seo}

      {/* Hero */}
      <div className="u-hero">
        <img className="u-hero__img" src="/design/assets/store-1.jpg" alt="Authentic Australian opals for sale at the Umoona opal store, Coober Pedy" />
        <div className="u-hero__scrim" />
        <div className="u-hero__inner">
          <div className="u-hero__eyebrow">Secure checkout · Powered by Stripe</div>
          <h1 className="u-hero__title">Secure Checkout</h1>
          <p className="u-hero__sub">
            Complete your purchase of {totalItems} items — enter your details and
            pay securely with Stripe. Every opal ships with a certificate of
            authenticity.
          </p>
        </div>
      </div>

      {/* Checkout flow */}
      <section className="u-sectpad u-sect-deep">
        <Link to="/opal-store" className="ck-back">
          <ArrowLeft size={15} />
          Back to Store
        </Link>

        {/* Messages */}
        {error && (
          <div className="ck-alert ck-alert--error" role="alert">
            <AlertCircle size={17} />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="ck-alert ck-alert--success" role="status">
            <CheckCircle size={17} />
            <span>{success}</span>
          </div>
        )}

        <div className="u-split ck-layout">
          {/* Left: customer information + delivery address */}
          <div>
            {/* Customer Information */}
            <div className="ck-card">
              <div className="ck-card__head">
                <span className="ck-card__icon">
                  <User size={19} />
                </span>
                <span className="ck-card__title">Customer Information</span>
              </div>

              <div className="ck-grid2">
                <label className="ck-field" htmlFor="firstName">
                  <span className="ck-label">
                    First Name <span className="ck-label__req">*</span>
                  </span>
                  <input
                    id="firstName"
                    className="ck-input"
                    type="text"
                    placeholder="John"
                    value={customerInfo.firstName}
                    onChange={(e) => handleInputChange("firstName", e.target.value)}
                    required
                  />
                </label>

                <label className="ck-field" htmlFor="lastName">
                  <span className="ck-label">
                    Last Name <span className="ck-label__req">*</span>
                  </span>
                  <input
                    id="lastName"
                    className="ck-input"
                    type="text"
                    placeholder="Smith"
                    value={customerInfo.lastName}
                    onChange={(e) => handleInputChange("lastName", e.target.value)}
                    required
                  />
                </label>
              </div>

              <label className="ck-field" htmlFor="email">
                <span className="ck-label">
                  <Mail size={14} /> Email Address{" "}
                  <span className="ck-label__req">*</span>
                </span>
                <input
                  id="email"
                  className="ck-input"
                  type="email"
                  placeholder="john.smith@example.com"
                  value={customerInfo.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  required
                />
              </label>

              <label className="ck-field ck-field--last" htmlFor="phone">
                <span className="ck-label">
                  <Phone size={14} /> Phone Number{" "}
                  <span className="ck-label__req">*</span>
                </span>
                <input
                  id="phone"
                  className="ck-input"
                  type="tel"
                  placeholder="04XX XXX XXX"
                  value={customerInfo.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  required
                />
              </label>
            </div>

            {/* Delivery Address */}
            <div className="ck-card">
              <div className="ck-card__head">
                <span className="ck-card__icon">
                  <MapPin size={19} />
                </span>
                <span className="ck-card__title">Delivery Address</span>
              </div>

              <div className="ck-shipnote">
                <Truck size={15} />
                <span>
                  {formatPrice(shippingFee)} flat rate shipping to all Australian
                  locations
                </span>
              </div>

              <label className="ck-field" htmlFor="addressLine1">
                <span className="ck-label">
                  Street Address <span className="ck-label__req">*</span>
                </span>
                <input
                  id="addressLine1"
                  className="ck-input"
                  type="text"
                  placeholder="123 Main Street"
                  value={customerInfo.addressLine1}
                  onChange={(e) => handleInputChange("addressLine1", e.target.value)}
                  required
                />
              </label>

              <label className="ck-field" htmlFor="addressLine2">
                <span className="ck-label">Apartment, Unit, etc. (Optional)</span>
                <input
                  id="addressLine2"
                  className="ck-input"
                  type="text"
                  placeholder="Unit 5"
                  value={customerInfo.addressLine2}
                  onChange={(e) => handleInputChange("addressLine2", e.target.value)}
                />
              </label>

              <div className="ck-grid2">
                <label className="ck-field" htmlFor="city">
                  <span className="ck-label">
                    City/Suburb <span className="ck-label__req">*</span>
                  </span>
                  <input
                    id="city"
                    className="ck-input"
                    type="text"
                    placeholder="Sydney"
                    value={customerInfo.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    required
                  />
                </label>

                <label className="ck-field" htmlFor="postalCode">
                  <span className="ck-label">
                    Postal Code <span className="ck-label__req">*</span>
                  </span>
                  <input
                    id="postalCode"
                    className="ck-input"
                    type="text"
                    placeholder="2000"
                    maxLength="4"
                    value={customerInfo.postalCode}
                    onChange={(e) => handleInputChange("postalCode", e.target.value)}
                    required
                  />
                </label>
              </div>

              <label className="ck-field" htmlFor="state">
                <span className="ck-label">
                  State/Territory <span className="ck-label__req">*</span>
                </span>
                <select
                  id="state"
                  className="ck-select"
                  value={customerInfo.state}
                  onChange={(e) => handleInputChange("state", e.target.value)}
                >
                  {australianStates.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.name} ({state.code})
                    </option>
                  ))}
                </select>
              </label>

              <label className="ck-field ck-field--last" htmlFor="deliveryNotes">
                <span className="ck-label">Delivery Instructions (Optional)</span>
                <textarea
                  id="deliveryNotes"
                  className="ck-textarea"
                  rows="3"
                  placeholder="Special delivery instructions, gate codes, etc."
                  value={customerInfo.deliveryNotes}
                  onChange={(e) => handleInputChange("deliveryNotes", e.target.value)}
                />
              </label>
            </div>
          </div>

          {/* Right: sticky order summary */}
          <aside className="ck-summary">
            <div className="ck-summary__title">
              <ShoppingCart size={18} />
              Order Summary
            </div>

            <div className="ck-items">
              {items.map((item) => {
                const price = parseFloat(
                  item.product.price || item.product.regular_price || 0
                );
                const itemTotal = price * item.quantity;
                const productId = item.product._id || item.product.id;

                return (
                  <div key={productId} className="ck-item">
                    <div className="ck-item__thumb">
                      {item.product.images && item.product.images.length > 0 ? (
                        <img
                          src={item.product.images[0].src || item.product.images[0]}
                          alt={item.product.name}
                          onError={(e) => {
                            e.target.src = "https://placehold.co/64/64";
                          }}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <ShoppingCart size={20} />
                      )}
                    </div>
                    <div className="ck-item__info">
                      <div className="ck-item__name">{item.product.name}</div>
                      <div className="ck-item__meta">
                        {formatPrice(price)} × {item.quantity}
                      </div>
                    </div>
                    <div className="ck-item__total">{formatPrice(itemTotal)}</div>
                  </div>
                );
              })}
            </div>

            <div className="ck-rows">
              <div className="ck-row">
                <span>Subtotal ({totalItems} items)</span>
                <span className="ck-row__val">{formatPrice(subtotal)}</span>
              </div>
              <div className="ck-row">
                <span>Shipping to Australia</span>
                <span className="ck-row__val">
                  {shippingFee === 0 ? "FREE" : formatPrice(shippingFee)}
                </span>
              </div>
            </div>

            <div className="ck-total">
              <span>Total</span>
              <span className="ck-total__val">{formatPrice(total)}</span>
            </div>

            <button
              type="button"
              className="ck-pay"
              onClick={handleCheckout}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="ck-spin" />
                  Creating secure checkout...
                </>
              ) : (
                <>
                  <Shield size={18} />
                  Pay with Stripe - {formatPrice(total)}
                </>
              )}
            </button>

            <div className="ck-secure">🔒 Secure 256-bit SSL encryption</div>
          </aside>
        </div>
      </section>

      {/* Trust / info — carries the old page's shipping + certificate copy */}
      <section className="u-sectpad u-sect-test">
        <div className="u-eyebrow">Every order includes</div>
        <h2 className="u-h2">Shipped safely, certified genuine</h2>
        <div className="u-grid3">
          <div className="ck-trust">
            <div className="ck-trust__head">
              <span className="ck-trust__icon">
                <Truck size={18} />
              </span>
              <span className="ck-trust__title">Shipping Information</span>
            </div>
            <ul className="ck-trust__list">
              <li>
                {formatPrice(shippingFee)} flat rate shipping to all Australian
                locations
              </li>
              <li>Orders processed within 1-2 business days</li>
              <li>Tracking information provided via email</li>
              <li>Secure packaging for safe delivery</li>
            </ul>
          </div>

          <div className="ck-trust">
            <div className="ck-trust__head">
              <span className="ck-trust__icon ck-trust__icon--teal">
                <Award size={18} />
              </span>
              <span className="ck-trust__title">Certificate of Authenticity</span>
            </div>
            <ul className="ck-trust__list ck-trust__list--teal">
              <li>Every opal comes with authenticity certificate</li>
              <li>Direct from Umoona Opal Mine, Coober Pedy</li>
              <li>100% genuine Australian opals</li>
              <li>Quality guarantee and satisfaction promise</li>
            </ul>
          </div>

          <div className="ck-trust">
            <div className="ck-trust__head">
              <span className="ck-trust__icon ck-trust__icon--cyan">
                <Shield size={18} />
              </span>
              <span className="ck-trust__title">Secure Checkout</span>
            </div>
            <ul className="ck-trust__list ck-trust__list--cyan">
              <li>Payments processed securely by Stripe</li>
              <li>Secure 256-bit SSL encryption</li>
              <li>We never store your card details</li>
              <li>Flat-rate shipping to all Australian locations</li>
            </ul>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
};

export default RCheckoutPage;
